import { useToast } from '@evisort/eds/components/Toast/hooks';
import useInterval from '@use-it/interval';
import { noop } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { useStableCallback } from '~/hooks/useStableCallback';

import * as toastTypes from '../types/toast.types';
import {
  CancellablePromise,
  createCancellablePromise,
} from '../utils/createCancellablePromise';

export interface Options {
  condition: boolean;
  deps: any[];
  errorHandler: (error: Error) => void;
  errorToastMessage: string;
  intervalMs: number | null;
  successHandler: (response: any) => void;
  successToastMessage: string;
  successToastType: any; // Note: this was unintentionally introduced so leaving a note to deprecate/remove this accordingly.
}

interface State<R> {
  isLoading: boolean;
  error?: Error;
  response?: R;
}

interface HooksData<R> extends State<R> {
  executor: () => void;
}

function useDepsVersion(deps: unknown[]) {
  const previousDepsRef = useRef(deps);
  const versionRef = useRef(0);
  const previousDeps = previousDepsRef.current;
  const hasChanged =
    previousDeps.length !== deps.length ||
    deps.some((dep, index) => !Object.is(dep, previousDeps[index]));

  if (hasChanged) {
    previousDepsRef.current = deps;
    versionRef.current += 1;
  }

  return versionRef.current;
}

/**
 * Reusable effect hook to process async callbacks.
 * - Provide a callback and params to run when condition is true.
 * - Rerun the hooks when provided deps change.
 * - Capture response and error in relevant callbacks.
 * - Conveniently render toast status via the errorToastMessage and successToastMessage options.
 * - Returns the response, isLoading, and error fields for the request.
 */
export const useAsync = <P, _T, R>(
  callback: (params: P) => Promise<R>,
  params: P,
  options: Partial<Options>,
): HooksData<R> => {
  const { toast } = useToast();
  const {
    condition = false,
    deps = [],
    errorHandler = noop,
    errorToastMessage,
    intervalMs,
    successHandler = noop,
    successToastMessage,
    successToastType = toastTypes.SUCCESS,
  } = options;

  const [shouldExecute, setShouldExecute] = useState(false);
  const [state, setState] = useState<State<R>>({ isLoading: false });

  function executor() {
    setShouldExecute(true);
  }

  const executeAsync = () => {
    let cancellablePromise: CancellablePromise<R>;
    if (shouldExecute || condition) {
      setState((prevState) => ({ ...prevState, isLoading: true }));
      cancellablePromise = createCancellablePromise(callback(params));
      cancellablePromise.promise
        .then((response) => {
          setState((prevState) => ({
            ...prevState,
            response,
            isLoading: false,
          }));
          successHandler(response);
          successToastMessage &&
            toast({
              text: successToastMessage,
              status:
                successToastType === toastTypes.SUCCESS
                  ? 'positive'
                  : 'informational',
            });
        })
        .catch((error) => {
          if (!error.cancelled) {
            setState((prevState) => ({
              ...prevState,
              error,
              isLoading: false,
            }));
            errorHandler(error);
            let errorToastMessageWithReponse = errorToastMessage;
            if (error.response) {
              const { data: resp_error_msg } = error.response.data;
              const errorContext = resp_error_msg ? `(${resp_error_msg})` : '';
              errorToastMessageWithReponse = `${
                errorToastMessageWithReponse ?? ''
              }${errorContext}`;
            }
            errorToastMessageWithReponse &&
              toast({
                status: 'critical',
                text: errorToastMessageWithReponse,
              });
          }
        })
        .finally(() => {
          setShouldExecute(false);
        });

      return () => {
        if (cancellablePromise) {
          cancellablePromise.cancel();
        }
        setShouldExecute(false);
      };
    }
  };

  const executeAsyncWithInterval = () => {
    if (intervalMs) {
      executeAsync();
    }
  };

  const runAsync = useStableCallback(executeAsync);
  const depsVersion = useDepsVersion(deps);

  useEffect(runAsync, [depsVersion, shouldExecute, runAsync]);

  useInterval(executeAsyncWithInterval, intervalMs || null);

  return { ...state, executor };
};
